import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { EmptyState } from '../components/EmptyState.js';
import { GlowCard } from '../components/GlowCard.js';

type ContentStatus = 'pending' | 'queued' | 'processing' | 'scheduled' | 'published' | 'failed';

interface PlatformCopy {
  caption: string;
  hashtags: string[];
  cta?: string;
}

type DistributionStatus = 'success' | 'failed' | 'skipped';

interface PlatformDistributionResult {
  status: DistributionStatus;
  error?: string;
  publishedAt?: string;
  postId?: string;
}

interface DistributionResults {
  platforms: Record<string, PlatformDistributionResult>;
  completedAt: string;
}

interface ContentItem {
  id: string;
  pipelineId: string;
  pipelineName: string;
  status: ContentStatus;
  title: string | null;
  description: string | null;
  sourceUrl: string | null;
  thumbnailUrl: string | null;
  generatedCopy: Record<string, PlatformCopy> | null;
  errorMessage: string | null;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
  distributionResults: DistributionResults | null;
}

const STATUS_STYLES: Record<ContentStatus, string> = {
  pending: 'bg-white/5 text-white/60',
  queued: 'bg-blue-500/10 text-blue-400 ring-1 ring-blue-500/20',
  processing: 'bg-yellow-500/10 text-yellow-400 ring-1 ring-yellow-500/20',
  scheduled: 'bg-purple-500/10 text-purple-400 ring-1 ring-purple-500/20',
  published: 'bg-green-500/10 text-green-400 ring-1 ring-green-500/20',
  failed: 'bg-red-500/10 text-red-400 ring-1 ring-red-500/20',
};

const PLATFORM_LABELS: Record<string, string> = {
  youtube: 'YouTube',
  instagram: 'Instagram',
  facebook: 'Facebook',
  linkedin: 'LinkedIn',
  tiktok: 'TikTok',
};

const PLATFORM_COLORS: Record<string, string> = {
  youtube: 'bg-red-500/20 text-red-400',
  instagram: 'bg-pink-500/20 text-pink-400',
  facebook: 'bg-blue-500/20 text-blue-400',
  linkedin: 'bg-sky-500/20 text-sky-400',
  tiktok: 'bg-white/10 text-white',
};

const PLATFORM_ABBR: Record<string, string> = {
  youtube: 'YT',
  instagram: 'IG',
  facebook: 'FB',
  linkedin: 'LI',
  tiktok: 'TK',
};

const DISTRIBUTION_STATUS_STYLES: Record<DistributionStatus, string> = {
  success: 'bg-green-500/10 text-green-400 ring-1 ring-green-500/20',
  failed: 'bg-red-500/10 text-red-400 ring-1 ring-red-500/20',
  skipped: 'bg-white/5 text-white/50',
};

const STATUS_FILTERS: { value: ContentStatus | 'all'; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'queued', label: 'Ready' },
  { value: 'processing', label: 'Processing' },
  { value: 'pending', label: 'Pending' },
  { value: 'failed', label: 'Failed' },
  { value: 'published', label: 'Published' },
];

function CopyBlock({ platform, copy }: { platform: string; copy: PlatformCopy }) {
  const [copied, setCopied] = useState(false);

  const fullText = [
    copy.caption,
    copy.hashtags.length > 0 ? copy.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ') : '',
    copy.cta ?? '',
  ]
    .filter(Boolean)
    .join('\n\n');

  async function handleCopy() {
    await navigator.clipboard.writeText(fullText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  return (
    <div className="rounded-lg border border-white/5 bg-pix-void p-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span
            className={[
              'inline-flex h-6 w-6 items-center justify-center rounded-md text-xs font-bold',
              PLATFORM_COLORS[platform] ?? 'bg-white/5 text-white/60',
            ].join(' ')}
          >
            {PLATFORM_ABBR[platform] ?? platform.slice(0, 2).toUpperCase()}
          </span>
          <span className="text-xs font-semibold text-white/70">
            {PLATFORM_LABELS[platform] ?? platform}
          </span>
        </div>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-white/50 hover:bg-white/10 hover:text-white/70"
        >
          {copied ? (
            <>
              <svg className="h-3.5 w-3.5 text-green-500" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Copied
            </>
          ) : (
            <>
              <svg className="h-3.5 w-3.5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
              </svg>
              Copy
            </>
          )}
        </button>
      </div>
      <p className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-white/60">
        {copy.caption}
      </p>
      {copy.hashtags.length > 0 && (
        <p className="mt-1 text-xs text-pix-blue">
          {copy.hashtags.map((h) => (h.startsWith('#') ? h : `#${h}`)).join(' ')}
        </p>
      )}
      {copy.cta && (
        <p className="mt-1 text-xs font-medium text-white/70">{copy.cta}</p>
      )}
    </div>
  );
}

function DistributionResultsBlock({ results }: { results: DistributionResults }) {
  return (
    <div className="mt-4 space-y-2">
      <p className="text-xs font-semibold uppercase tracking-wide text-white/40">
        Distribution results
      </p>
      {Object.entries(results.platforms).map(([platform, result]) => (
        <div key={platform} className="flex items-start gap-3 rounded-lg border border-white/5 bg-pix-void px-3 py-2">
          <span
            className={[
              'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-xs font-bold',
              PLATFORM_COLORS[platform] ?? 'bg-white/5 text-white/60',
            ].join(' ')}
          >
            {PLATFORM_ABBR[platform] ?? platform.slice(0, 2).toUpperCase()}
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-white/70">
                {PLATFORM_LABELS[platform] ?? platform}
              </span>
              <span
                className={[
                  'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
                  DISTRIBUTION_STATUS_STYLES[result.status],
                ].join(' ')}
              >
                {result.status === 'success' ? 'Published' : result.status === 'failed' ? 'Failed' : 'Skipped'}
              </span>
            </div>
            {result.publishedAt && (
              <p className="mt-0.5 text-xs text-white/40">
                {new Date(result.publishedAt).toLocaleString()}
              </p>
            )}
            {result.error && (
              <p className="mt-0.5 text-xs text-red-400 truncate" title={result.error}>
                {result.error.length > 80 ? `${result.error.slice(0, 80)}...` : result.error}
              </p>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function ContentCard({
  item,
  onPublish,
  publishing,
}: {
  item: ContentItem;
  onPublish: (id: string) => void;
  publishing: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  const hasCopy = item.generatedCopy && Object.keys(item.generatedCopy).length > 0;
  const isProcessing = item.status === 'processing';
  const canPublish = hasCopy && (item.status === 'queued' || item.status === 'processing');
  const hasDistributionResults = !!item.distributionResults;

  return (
    <GlowCard className="overflow-hidden rounded-xl border border-white/10 bg-pix-deep">
      <button
        onClick={() => setExpanded((v) => !v)}
        className="flex w-full items-start gap-4 px-5 py-4 text-left"
      >
        {/* Thumbnail */}
        <div className="h-14 w-14 shrink-0 overflow-hidden rounded-lg bg-white/5">
          {item.thumbnailUrl ? (
            <img src={item.thumbnailUrl} alt="" className="h-full w-full object-cover" />
          ) : (
            <div className="flex h-full w-full items-center justify-center">
              <svg className="h-6 w-6 text-gray-300" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0121 8.68v6.64a1 1 0 01-1.447.894L15 14M4 8a2 2 0 00-2 2v4a2 2 0 002 2h8a2 2 0 002-2v-4a2 2 0 00-2-2H4z" />
              </svg>
            </div>
          )}
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-medium text-white">
            {item.title ?? 'Untitled'}
          </p>
          <div className="mt-1 flex flex-wrap items-center gap-2">
            <span className="text-xs text-white/40">{item.pipelineName}</span>
            <span className="text-gray-300">·</span>
            <span className="text-xs text-white/40">
              {new Date(item.createdAt).toLocaleDateString()}
            </span>
          </div>
        </div>

        {/* Status + publish + chevron */}
        <div className="ml-2 flex shrink-0 items-center gap-2">
          {isProcessing && (
            <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
          )}
          {canPublish && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                onPublish(item.id);
              }}
              disabled={publishing}
              className="rounded-lg bg-pix-pink px-3 py-1.5 text-xs font-medium text-white transition-opacity hover:bg-pix-pink-dark disabled:opacity-50"
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </button>
          )}
          <span
            className={[
              'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium',
              STATUS_STYLES[item.status],
            ].join(' ')}
          >
            {item.status}
          </span>
          <svg
            className={[
              'h-4 w-4 text-white/40 transition-transform duration-150',
              expanded ? 'rotate-180' : '',
            ].join(' ')}
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-white/5 px-5 pb-5 pt-4">
          {item.errorMessage && (
            <div className="mb-3 rounded-lg bg-red-500/10 px-3 py-2 text-xs text-red-400">
              Error: {item.errorMessage}
            </div>
          )}

          {isProcessing && (
            <div className="flex items-center gap-2 text-xs text-yellow-400">
              <div className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-yellow-500 border-t-transparent" />
              Generating copy...
            </div>
          )}

          {hasCopy && item.generatedCopy && (
            <div className="space-y-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-white/40">
                Generated copy
              </p>
              {Object.entries(item.generatedCopy).map(([platform, copy]) => (
                <CopyBlock key={platform} platform={platform} copy={copy} />
              ))}
            </div>
          )}

          {hasDistributionResults && item.distributionResults && (
            <DistributionResultsBlock results={item.distributionResults} />
          )}

          {!hasCopy && !isProcessing && !item.errorMessage && (
            <p className="text-xs text-white/40">No copy generated yet.</p>
          )}

          {item.sourceUrl && (
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="mt-3 inline-flex items-center gap-1 text-xs text-pix-blue hover:text-pix-blue/80"
            >
              View source
              <svg className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </a>
          )}
        </div>
      )}
    </GlowCard>
  );
}

export default function ContentQueuePage() {
  const [items, setItems] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<ContentStatus | 'all'>('all');
  const [publishing, setPublishing] = useState<string | null>(null);

  useEffect(() => {
    loadItems();
  }, []);

  async function loadItems() {
    setLoading(true);
    setError(null);
    const result = await api.get<ContentItem[]>('/api/content-items');
    if (result.data) {
      setItems(result.data);
    } else {
      setError('Failed to load content items.');
    }
    setLoading(false);
  }

  async function handlePublish(id: string) {
    setPublishing(id);
    const result = await api.post<{ id: string; status: string }>(`/api/content-items/${id}/publish`, {});
    if (result.data) {
      setItems((prev) =>
        prev.map((item) => (item.id === id ? { ...item, status: 'queued' as ContentStatus } : item))
      );
    } else {
      setError('Failed to trigger publish. Please try again.');
    }
    setPublishing(null);
  }

  const filtered =
    statusFilter === 'all' ? items : items.filter((i) => i.status === statusFilter);

  return (
    <div className="max-w-3xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Content Queue</h1>
          <p className="mt-1 text-sm text-white/50">
            AI-generated copy ready for distribution.
          </p>
        </div>
        <button
          onClick={loadItems}
          title="Refresh"
          className="rounded-lg border border-white/10 p-2 text-white/50 hover:bg-pix-void hover:text-white/70"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      {/* Status filter tabs */}
      <div className="mt-4 flex flex-wrap gap-1">
        {STATUS_FILTERS.map((f) => {
          const count =
            f.value === 'all'
              ? items.length
              : items.filter((i) => i.status === f.value).length;
          return (
            <button
              key={f.value}
              onClick={() => setStatusFilter(f.value)}
              className={[
                'rounded-full px-3 py-1 text-xs font-medium transition-colors',
                statusFilter === f.value
                  ? 'bg-pix-pink text-white'
                  : 'bg-white/5 text-white/60 hover:bg-white/10',
              ].join(' ')}
            >
              {f.label}
              {count > 0 && (
                <span className="ml-1 opacity-70">{count}</span>
              )}
            </button>
          );
        })}
      </div>

      {error && (
        <div className="mt-4 rounded-lg bg-red-500/10 px-4 py-3 text-sm text-red-400 ring-1 ring-red-500/20">
          {error}
        </div>
      )}

      <div className="mt-4 space-y-3">
        {loading ? (
          <div className="flex h-32 items-center justify-center">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-pix-pink border-t-transparent" />
          </div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={
              <svg className="h-6 w-6" fill="none" stroke="currentColor" strokeWidth={1.5} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M3.375 19.5h17.25m-17.25 0a1.125 1.125 0 01-1.125-1.125M3.375 19.5h1.5C5.496 19.5 6 18.996 6 18.375m-3.75.125v-3.375m0 0a1.125 1.125 0 011.125-1.125h12.75A1.125 1.125 0 0117.25 15v3.375M3.375 15h13.5M3.375 15v-3.375a1.125 1.125 0 011.125-1.125h12.75A1.125 1.125 0 0118.75 12v3.375" />
              </svg>
            }
            headline={statusFilter === 'all' ? 'No content items yet' : `No ${statusFilter} items`}
            subtext={statusFilter === 'all' ? 'Activate a pipeline and run it to start ingesting TikTok content.' : `No content items with status "${statusFilter}".`}
          />
        ) : (
          filtered.map((item) => (
            <ContentCard
              key={item.id}
              item={item}
              onPublish={handlePublish}
              publishing={publishing === item.id}
            />
          ))
        )}
      </div>
    </div>
  );
}
